/**
 * Fixture data + transport for `?fixture=report` — a RAW `GET
 * /api/cockpit/reports?weekStart=` envelope in the exact shape
 * `azure-functions/cis/CisCockpitReports/index.js` produces (per-project
 * `readiness` from `shared/cockpit.js` `readinessView` + `reports` from
 * `shared/report-state.js` `reportRow`), run through the same
 * `shapeReportPage` pipeline (src/features/sm/api/shape.js) the live app
 * uses, plus an in-memory confirm/approve/reopen/publish store so the screen
 * is fully testable offline (SPEC-CIS-SITE-MANAGER-COCKPIT-V3-001 §4.6).
 *
 * Visible text (project codes, report numbers, dates, states) follows
 * mockups/cockpit-v3/Report.dc.html as closely as the real data contract
 * allows: the artboard's readiness checklist format (X/Y counts, an item
 * list, a fourth "Progress entered" line) and its SS411 confirmation
 * timestamps are illustrative-only and not reproduced here — see
 * shape.js's REPORT_LINE_LABELS doc comment and v-artboard.deviations.json.
 *
 * weekStart 2026-08-31 is a Monday (W36) — the same "today" week
 * fixtures/main.js's own weekLabel ("W36 · Wed 2 Sep") and fixtures/plan.js
 * use, so Report always opens on the current week like Plan and Today do
 * (owner review 2026-10: Report had been left pinned one week behind the
 * rest of the fixture world — its own header read "Week 35" with a "Back
 * to this week" control, even on first load). W35 = 2026-08-24 (one week
 * back), W34 = 2026-08-17 (two weeks back).
 */

"use strict";

import { formatPeriodLabel, periodKey } from "../api/reportPeriod.js";
import { buildFixtureV2Projects, V2_CURRENT_WEEK, V2_TODAY, V2_ME } from "./report-v2.js";

const CURRENT_WEEK_V1 = "2026-08-31"; // W36
const CURRENT_WEEK = CURRENT_WEEK_V1;
const W35 = "2026-08-24";
const W34 = "2026-08-17";

function docxUrl(code, reportNumber) {
  return `https://negmarine.sharepoint.com/sites/ConstructionSupervision/Weekly%20Reports/${code}/${code}-WR-${reportNumber}.docx`;
}

function versionUrl(code, reportNumber, versionId) {
  return `/api/cockpit/reports?code=${code}&reportNumber=${reportNumber}&versionId=${versionId}`;
}

function reportRow(overrides) {
  const { code, reportNumber } = overrides;
  return {
    reportNumber,
    weekStart: overrides.weekStart,
    // SPEC-CIS-REPORT-CUSTOM-PERIOD-001 section 4.2: only a custom report in
    // the period-aware fixture carries these (a plain week row has none, as
    // on a real row written before the period columns).
    ...(overrides.periodKind === "custom"
      ? {
          periodStart: overrides.periodStart,
          periodEnd: overrides.periodEnd,
          periodKind: "custom",
          periodLabel: formatPeriodLabel(overrides.periodStart, overrides.periodEnd),
        }
      : {}),
    reportStatus: overrides.reportStatus,
    // Only an Issued report carries the number a person sees.
    publishedNumber: overrides.reportStatus === "Issued" ? overrides.publishedNumber || null : null,
    generationCount: overrides.generationCount || 1,
    dataConfirmedAtUtc: overrides.dataConfirmedAtUtc || "",
    dataConfirmedBy: overrides.dataConfirmedBy || "",
    approvedAtUtc: overrides.approvedAtUtc || "",
    approvedBy: overrides.approvedBy || "",
    issuedAtUtc: overrides.issuedAtUtc || "",
    publishedUrl: overrides.publishedUrl || "",
    etag: overrides.etag || `"etag-${code}-${reportNumber}"`,
    // DEC-CHAT-20260928-202: mirrors shared/report-state.js reportRow's own
    // field — every fixture report represents a normal generation that DID
    // land prose unless a sample explicitly overrides it to "" to exercise
    // the "no prose" archive-row case.
    llmRunId: overrides.llmRunId !== undefined ? overrides.llmRunId : "fixture-llm-run",
    docxUrl: overrides.docxUrl !== undefined ? overrides.docxUrl : docxUrl(code, reportNumber),
    versions: overrides.versions || [{ versionId: "1", createdAtUtc: overrides.generatedAtUtc || "2026-08-21T12:00:00.000Z", url: versionUrl(code, reportNumber, "1") }],
  };
}

// readinessHash values below are illustrative fixture-only sha256 hex
// strings (64 hex chars, matching the real format validated by
// CisReportWeekly/index.js: /^[a-f0-9]{64}$/) — never recomputed client-side
// (shape.js buildReportConfirmBody sends exactly what the read returned).
const HASH_SS412413 = "1".repeat(60) + "aaaa"; // exactly 64 hex chars, as CisReportWeekly requires
const HASH_SS418 = "2".repeat(60) + "bbbb";
const HASH_SS411 = "3".repeat(60) + "cccc";

function buildProjects() {
  return {
    SS412413: {
      code: "SS412413",
      readiness: {
        weekStart: CURRENT_WEEK,
        lines: [
          { id: "inspection_results", count: 0, green: true },
          // `items` (readinessView, shared/cockpit.js — additive, never part
          // of readinessHash) was missing from this fixture line entirely,
          // which made the detail panel show the false all-clear sentence
          // ("Every inspection has a photo or an acceptance.") under a red,
          // count:2 line — Report.jsx's ChecklistDetail now also renders a
          // "list not available yet" fallback for that mismatch defensively
          // (see ChecklistDetail), but the real fix is this fixture
          // reflecting what the real backend line actually carries.
          { id: "inspection_photos", count: 2, green: false, items: [
            // SPEC-CIS-COCKPIT-READINESS-ACTIONS-001 §4.1 detail fields, as the real readinessView now returns them.
            { inspectionNumber: 1821, title: "Ballast tank coating DFT", inspectionDate: "2026-08-26", inspector: { upn: "inspector1@example.com" }, category: "Painting", scheduleRef: "", etag: '"fixture-1821"', photoWaiver: null },
            { inspectionNumber: 1834, title: "Main engine chock fit", inspectionDate: "2026-08-27", inspector: { upn: "inspector2@example.com" }, category: "Machinery Outfitting", scheduleRef: "", etag: '"fixture-1834"', photoWaiver: null },
          ] },
          { id: "ncr_decisions", count: 0, green: true },
        ],
        readinessHash: HASH_SS412413,
        allGreen: false,
      },
      reports: [
        reportRow({ code: "SS412413", reportNumber: 43, weekStart: W35, reportStatus: "Issued", publishedNumber: 2, generationCount: 1, issuedAtUtc: "2026-08-31T10:00:00.000Z", approvedAtUtc: "2026-08-30T08:00:00.000Z", approvedBy: "s.larsen@example.com" }),
        reportRow({
          code: "SS412413",
          reportNumber: 42,
          weekStart: W34,
          reportStatus: "Issued",
          publishedNumber: 1,
          generationCount: 2,
          issuedAtUtc: "2026-08-24T10:00:00.000Z",
          approvedAtUtc: "2026-08-23T08:00:00.000Z",
          approvedBy: "s.larsen@example.com",
          versions: [
            { versionId: "1", createdAtUtc: "2026-08-21T09:00:00.000Z", url: versionUrl("SS412413", 42, "1") },
            { versionId: "2", createdAtUtc: "2026-08-22T09:00:00.000Z", url: versionUrl("SS412413", 42, "2") },
          ],
        }),
      ],
    },
    SS418: {
      code: "SS418",
      readiness: {
        weekStart: CURRENT_WEEK,
        lines: [
          { id: "inspection_results", count: 0, green: true },
          { id: "inspection_photos", count: 0, green: true },
          { id: "ncr_decisions", count: 0, green: true },
        ],
        readinessHash: HASH_SS418,
        allGreen: true,
      },
      reports: [
        reportRow({
          code: "SS418",
          reportNumber: 39,
          weekStart: W35,
          reportStatus: "Issued",
          publishedNumber: 1,
          generationCount: 2,
          issuedAtUtc: "2026-08-31T10:00:00.000Z",
          approvedAtUtc: "2026-08-30T08:00:00.000Z",
          approvedBy: "s.larsen@example.com",
          versions: [
            { versionId: "1", createdAtUtc: "2026-08-28T09:00:00.000Z", url: versionUrl("SS418", 39, "1") },
            { versionId: "2", createdAtUtc: "2026-08-29T09:00:00.000Z", url: versionUrl("SS418", 39, "2") },
          ],
        }),
      ],
    },
    SS411: {
      code: "SS411",
      readiness: {
        weekStart: CURRENT_WEEK,
        lines: [
          { id: "inspection_results", count: 0, green: true },
          { id: "inspection_photos", count: 0, green: true },
          { id: "ncr_decisions", count: 0, green: true },
        ],
        readinessHash: HASH_SS411,
        allGreen: true,
      },
      reports: [
        reportRow({
          code: "SS411",
          reportNumber: 75,
          weekStart: CURRENT_WEEK,
          reportStatus: "Draft",
          generationCount: 1,
          dataConfirmedAtUtc: "2026-09-04T09:12:00.000Z",
          dataConfirmedBy: "s.larsen@example.com",
          generatedAtUtc: "2026-09-04T12:05:00.000Z",
          versions: [{ versionId: "1", createdAtUtc: "2026-09-04T12:05:00.000Z", url: versionUrl("SS411", 75, "1") }],
        }),
        reportRow({
          code: "SS411",
          reportNumber: 74,
          weekStart: W35,
          reportStatus: "Approved",
          generationCount: 1,
          dataConfirmedAtUtc: "2026-08-28T09:00:00.000Z",
          dataConfirmedBy: "s.larsen@example.com",
          approvedAtUtc: "2026-09-01T08:00:00.000Z",
          approvedBy: "s.larsen@example.com",
          versions: [{ versionId: "1", createdAtUtc: "2026-08-28T12:00:00.000Z", url: versionUrl("SS411", 74, "1") }],
        }),
        reportRow({
          code: "SS411",
          reportNumber: 73,
          weekStart: W34,
          reportStatus: "Issued",
          publishedNumber: 1,
          generationCount: 1,
          approvedAtUtc: "2026-08-24T08:00:00.000Z",
          approvedBy: "s.larsen@example.com",
          issuedAtUtc: "2026-08-25T09:00:00.000Z",
          versions: [{ versionId: "1", createdAtUtc: "2026-08-21T12:00:00.000Z", url: versionUrl("SS411", 73, "1") }],
        }),
      ],
    },
  };
}

export function buildFixtureReportsEnvelope() {
  const byCode = buildProjects();
  return {
    generatedAtUtc: "2026-09-09T09:00:00.000Z",
    weekStart: CURRENT_WEEK,
    errors: [],
    projects: Object.values(byCode),
  };
}

export const fixtureReportPageEnvelope = buildFixtureReportsEnvelope();
export const fixtureReportWeekStart = CURRENT_WEEK;

/**
 * `?fixture=report-period`: the same data, but each project's readiness
 * carries the period fields a period-aware backend returns (periodKind,
 * periodStart, periodEnd, coveredBy), so the screen offers the Custom period
 * control. The plain `?fixture=report` envelope above has none of them, so the
 * control stays hidden there and the artboard baseline is untouched.
 */
function withPeriodFields(envelope) {
  return {
    ...envelope,
    periodStart: envelope.weekStart,
    periodEnd: addDaysIso(envelope.weekStart, 6),
    periodKind: "week",
    projects: envelope.projects.map((p) => ({
      ...p,
      readiness: {
        ...p.readiness,
        periodStart: p.readiness.weekStart,
        periodEnd: addDaysIso(p.readiness.weekStart, 6),
        periodKind: "week",
        coveredBy: [],
      },
    })),
  };
}

function addDaysIso(iso, days) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export const fixtureReportPeriodPageEnvelope = withPeriodFields(buildFixtureReportsEnvelope());

/**
 * In-memory confirm/approve/reopen/publish store, same discipline as
 * fixtures/ncr.js#createFixtureNcrStore: a thin, independent
 * reimplementation of the real endpoints' shape rules (etag compare-and-
 * swap, transition matrix, reason-required reopen, dataConfirmation
 * hash check) so the screen's flows can be exercised end to end without a
 * network call. Publish is fixture-only in this build step — it stamps
 * `IssuedAtUtc`/`PublishedUrl` and returns a fictional NEG-internal-domain
 * URL rather than performing a real SharePoint copy.
 */
export function createFixtureReportStore({ period: periodAwareOption = false, dataset = "v1", state = "draft", genMs } = {}) {
  // dataset "v2" is the Weekly report screen v2 world (fixtures/report-v2.js):
  // the design's own sample, today = Sat 10 Oct 2026, always period-aware.
  const v2 = dataset === "v2";
  const periodAware = periodAwareOption || v2;
  const CURRENT_WEEK = v2 ? V2_CURRENT_WEEK : CURRENT_WEEK_V1;
  const TODAY = v2 ? V2_TODAY : "2026-09-02";
  const ME = v2 ? V2_ME : "sm@example.com";
  let byCode = v2 ? buildFixtureV2Projects(state) : buildProjects();
  let nextReportNumber = v2
    ? Object.fromEntries(Object.entries(byCode).map(([c, p]) => [c, Math.max(0, ...(p.reports || []).map((r) => r.reportNumber)) + 1]))
    : { SS412413: 44, SS418: 40, SS411: 76 };
  let jobSeq = 0;
  const jobs = new Map();
  const browser = typeof window !== "undefined" ? window : {};
  const delay = () => (Number(browser.__fixtureDelayMs) > 0 ? new Promise((r) => setTimeout(r, Number(browser.__fixtureDelayMs))) : Promise.resolve());
  // Every request the period-aware fixture serves, in order. Fixture mode
  // only: exposed on window so a browser test can read what App.jsx sent.
  const calls = [];
  if (periodAware && typeof window !== "undefined") window.__fixtureReportCalls = calls;

  function overlapConflict(period) {
    for (const project of Object.values(byCode)) {
      for (const r of project.reports || []) {
        if (r.reportStatus !== "Approved" && r.reportStatus !== "Issued") continue;
        const start = r.periodKind === "custom" ? r.periodStart : r.weekStart;
        const end = r.periodKind === "custom" ? r.periodEnd : addDaysIso(r.weekStart, 6);
        if (start <= period.periodEnd && period.periodStart <= end) {
          return { reportNumber: r.reportNumber, publishedNumber: r.reportStatus === "Issued" ? r.publishedNumber || null : null, periodStart: start, periodEnd: end, periodKind: r.periodKind === "custom" ? "custom" : "week", periodLabel: formatPeriodLabel(start, end), reportStatus: r.reportStatus };
        }
      }
    }
    return null;
  }

  /** The §4.1 refusals, in order, for a requested custom period (fixture "today" = the plan fixture's frozen day). */
  function periodRefusal(period) {
    const { periodStart, periodEnd } = period;
    const days = Math.round((Date.parse(`${periodEnd}T00:00:00Z`) - Date.parse(`${periodStart}T00:00:00Z`)) / 86400000) + 1;
    if (!(periodStart <= periodEnd)) return { status: 422, ok: false, body: { errorCode: "period_invalid" } };
    if (days > 92) return { status: 422, ok: false, body: { errorCode: "period_too_long" } };
    if (periodEnd > TODAY) return { status: 422, ok: false, body: { errorCode: "period_in_future" } };
    return null;
  }

  function periodReadiness(project, period) {
    const base = project.readiness;
    return {
      ...base,
      weekStart: period.periodStart,
      periodStart: period.periodStart,
      periodEnd: period.periodEnd,
      periodKind: "custom",
      // A period's hash is its own (the server hashes the period and the counts).
      readinessHash: `c${base.readinessHash.slice(1)}`,
      coveredBy: coveredByFor(project, period),
    };
  }

  function coveredByFor(project, range) {
    return (project.reports || [])
      .filter((r) => r.reportStatus !== "Withdrawn")
      .map((r) => {
        const start = r.periodKind === "custom" ? r.periodStart : r.weekStart;
        const end = r.periodKind === "custom" ? r.periodEnd : addDaysIso(r.weekStart, 6);
        return { reportNumber: r.reportNumber, periodStart: start, periodEnd: end, periodKind: r.periodKind === "custom" ? "custom" : "week", reportStatus: r.reportStatus };
      })
      .filter((c) => c.periodStart <= range.periodEnd && range.periodStart <= c.periodEnd);
  }

  async function fetchReports(weekStart, period) {
    const res = await fetchReportsRaw(weekStart, period);
    // Browser tests set window.__fixtureOmitGenSignal to mimic a server whose claim read failed (the field is omitted).
    if (browser.__fixtureOmitGenSignal && res.ok) res.body = { ...res.body, projects: res.body.projects.map(({ generationInProgress, ...rest }) => rest) };
    return res;
  }

  async function fetchReportsRaw(weekStart, period) {
    if (periodAware) calls.push({ call: "fetchReports", weekStart: weekStart || null, period: period || null });
    if (period) {
      const refused = periodRefusal(period);
      if (refused) return refused;
      const conflict = overlapConflict(period);
      const sameCustom = conflict && conflict.periodKind === "custom" && conflict.periodStart === period.periodStart && conflict.periodEnd === period.periodEnd;
      if (conflict && !sameCustom) return { status: 409, ok: false, body: { errorCode: "period_overlap", conflict } };
      return {
        status: 200,
        ok: true,
        body: {
          generatedAtUtc: new Date().toISOString(),
          weekStart: period.periodStart,
          periodStart: period.periodStart,
          periodEnd: period.periodEnd,
          periodKind: "custom",
          errors: [],
          projects: Object.values(byCode).map((p) => ({ ...p, readiness: periodReadiness(p, period) })),
        },
      };
    }
    const week = weekStart || CURRENT_WEEK;
    const envelope = {
      generatedAtUtc: new Date().toISOString(),
      weekStart: week,
      ...(periodAware ? { periodStart: week, periodEnd: addDaysIso(week, 6), periodKind: "week" } : {}),
      errors: [],
      projects: Object.values(byCode).map((p) => ({
        ...p,
        readiness: {
          ...p.readiness,
          weekStart: week,
          ...(periodAware
            ? { periodStart: week, periodEnd: addDaysIso(week, 6), periodKind: "week", coveredBy: coveredByFor(p, { periodStart: week, periodEnd: addDaysIso(week, 6) }) }
            : {}),
        },
      })),
    };
    return { status: 200, ok: true, body: envelope };
  }

  /** Waiver / remind in the period-aware fixture: the response carries the readiness of the view the call named. */
  function viewReadiness(project, weekStart, period) {
    return period ? periodReadiness(project, period) : { ...project.readiness, weekStart: weekStart || CURRENT_WEEK, periodKind: "week", periodStart: weekStart || CURRENT_WEEK, periodEnd: addDaysIso(weekStart || CURRENT_WEEK, 6), coveredBy: [] };
  }

  async function waivePhoto(code, inspectionNumber, etag, reason, weekStart, period) {
    calls.push({ call: "waivePhoto", code, inspectionNumber, weekStart: weekStart || null, period: period || null });
    const project = byCode[code];
    if (!project) return { status: 404, ok: false, body: { errorCode: "project_not_found" } };
    const line = project.readiness.lines.find((l) => l.id === "inspection_photos");
    const item = line && (line.items || []).find((i) => i.inspectionNumber === Number(inspectionNumber));
    if (!item) return { status: 404, ok: false, body: { errorCode: "inspection_not_found" } };
    line.items = line.items.filter((i) => i !== item);
    line.count = line.items.length;
    line.green = line.count === 0;
    project.readiness.allGreen = project.readiness.lines.every((l) => l.green);
    return { status: 200, ok: true, body: { photoWaivedBy: "sm@example.com", readiness: viewReadiness(project, weekStart, period) } };
  }

  async function remindPhoto(code, inspectionNumber, weekStart, period) {
    calls.push({ call: "remindPhoto", code, inspectionNumber, weekStart: weekStart || null, period: period || null });
    const project = byCode[code];
    if (!project) return { status: 404, ok: false, body: { errorCode: "project_not_found" } };
    return { status: 200, ok: true, body: { status: "sent", readiness: viewReadiness(project, weekStart, period) } };
  }

  function findReport(code, reportNumber) {
    return (byCode[code].reports || []).find((r) => r.reportNumber === Number(reportNumber));
  }

  /**
   * POST /api/projects/{code}/reports/weekly. With `async: true` (what the
   * screen sends) the answer is the 202 job envelope and the report appears
   * only when the "job" finishes (window.__fixtureGenMs, default 2500 ms);
   * until then `generationInProgress` is on the project and a second POST is
   * refused 409 generation_in_progress -- the one-claim-per-project rule.
   */
  async function postReportWeekly(code, body) {
    const project = byCode[code];
    if (!project) return { status: 404, ok: false, body: { errorCode: "project_not_found" } };
    if (project.generationInProgress) {
      if (periodAware) calls.push({ call: "postReportWeekly", code, refused: "generation_in_progress", async: Boolean(body && body.async) });
      return { status: 409, ok: false, body: { errorCode: "generation_in_progress", jobId: project.generationInProgress.jobId, weekStart: project.generationInProgress.weekStart } };
    }
    if (!(body && body.async)) return postReportWeeklySync(code, body);
    const before = structuredClone(project.reports || []);
    const result = await postReportWeeklySync(code, body);
    if (!result.ok) return result;
    const after = project.reports;
    project.reports = before;
    const jobId = `fixture-job-${++jobSeq}`;
    const customPeriod = body.periodStart && body.periodEnd ? { periodStart: body.periodStart, periodEnd: body.periodEnd, periodKind: "custom" } : {};
    project.generationInProgress = {
      jobId, startedAt: new Date().toISOString(), by: ME, state: "running", weekStart: body.weekStart, ...customPeriod,
      reportNumber: result.body.reportNumber,
    };
    jobs.set(jobId, { jobId, state: "running" });
    const ms = genMs !== undefined ? genMs : Number(browser.__fixtureGenMs) >= 0 && browser.__fixtureGenMs !== undefined ? Number(browser.__fixtureGenMs) : 2500;
    setTimeout(() => {
      project.reports = after;
      delete project.generationInProgress;
      jobs.set(jobId, { jobId, state: "ready", reportNumber: result.body.reportNumber });
    }, ms);
    if (periodAware) calls.push({ call: "generationStarted", code, jobId });
    return { status: 202, ok: true, body: { jobId, state: "queued", statusPath: `/api/projects/${code}/reports/weekly/status/${jobId}` } };
  }

  async function fetchReportStatus(code, jobId) {
    if (periodAware) calls.push({ call: "fetchReportStatus", code, jobId });
    const job = jobs.get(jobId);
    if (!job) return { status: 202, ok: true, body: { jobId, state: "running" } };
    return job.state === "running" ? { status: 202, ok: true, body: job } : { status: 200, ok: true, body: job };
  }

  /** Fixture reproduction of POST /api/projects/{code}/reports/weekly (§4.6 gate). */
  async function postReportWeeklySync(code, body) {
    const project = byCode[code];
    if (!project) return { status: 404, ok: false, body: { errorCode: "project_not_found" } };
    const customPeriod = body && body.periodStart && body.periodEnd ? { periodStart: body.periodStart, periodEnd: body.periodEnd } : null;
    if (periodAware) calls.push({ call: "postReportWeekly", code, weekStart: body.weekStart || null, period: customPeriod, readinessHash: body.dataConfirmation && body.dataConfirmation.readinessHash });
    if (customPeriod) {
      const refused = periodRefusal(customPeriod);
      if (refused) return refused;
      const conflict = overlapConflict(customPeriod);
      const sameCustom = conflict && conflict.periodKind === "custom" && conflict.periodStart === customPeriod.periodStart && conflict.periodEnd === customPeriod.periodEnd;
      if (conflict && !sameCustom) return { status: 409, ok: false, body: { errorCode: "period_overlap", conflict } };
      const periodRead = periodReadiness(project, customPeriod);
      const supplied = body.dataConfirmation;
      if (!supplied || supplied.weekStart !== customPeriod.periodStart || supplied.readinessHash !== periodRead.readinessHash || !periodRead.allGreen) {
        return { status: 422, ok: false, body: { errorCode: "data_incomplete", lines: periodRead.lines, readinessHash: periodRead.readinessHash } };
      }
      const existingCustom = (project.reports || []).find((r) => r.periodKind === "custom" && r.periodStart === customPeriod.periodStart && r.periodEnd === customPeriod.periodEnd);
      if (existingCustom) {
        if (existingCustom.reportStatus !== "Draft") return { status: 409, ok: false, body: { errorCode: "report_issued" } };
        if (!String(body.regenerateReason || "").trim()) return { status: 422, ok: false, body: { errorCode: "invalid_request" } };
        existingCustom.generationCount = (existingCustom.generationCount || 0) + 1;
        existingCustom.etag = `"etag-${code}-${existingCustom.reportNumber}-${Date.now()}"`;
        return { status: 200, ok: true, body: { reportNumber: existingCustom.reportNumber, reportStatus: "Draft", generationCount: existingCustom.generationCount, llmRunId: "fixture-run", ...customPeriod, periodKind: "custom" } };
      }
      const customNumber = nextReportNumber[code]++;
      const customNow = new Date().toISOString();
      const customRow = reportRow({
        code, reportNumber: customNumber, weekStart: customPeriod.periodStart, periodKind: "custom", ...customPeriod,
        reportStatus: "Draft", generationCount: 1, dataConfirmedAtUtc: customNow, dataConfirmedBy: "sm@example.com", generatedAtUtc: customNow,
        versions: [{ versionId: "1", createdAtUtc: customNow, url: versionUrl(code, customNumber, "1") }],
      });
      project.reports = [customRow, ...(project.reports || [])];
      return { status: 200, ok: true, body: { reportNumber: customNumber, reportStatus: "Draft", generationCount: 1, llmRunId: "fixture-run", ...customPeriod, periodKind: "custom" } };
    }
    const readiness = project.readiness;
    const supplied = body && body.dataConfirmation;
    if (!supplied || supplied.weekStart !== body.weekStart || !/^[a-f0-9]{64}$/.test(String(supplied.readinessHash || ""))) {
      return { status: 422, ok: false, body: { errorCode: "data_incomplete", lines: readiness.lines } };
    }
    if (!readiness.allGreen || supplied.readinessHash !== readiness.readinessHash) {
      return { status: 422, ok: false, body: { errorCode: "data_incomplete", lines: readiness.lines, readinessHash: readiness.readinessHash } };
    }
    const existing = (project.reports || []).find((r) => r.periodKind !== "custom" && r.weekStart === body.weekStart);
    if (existing) {
      if (existing.reportStatus !== "Draft") {
        return { status: 409, ok: false, body: { errorCode: "report_issued" } };
      }
      if ((existing.generationCount || 0) >= 1 && !String(body.regenerateReason || "").trim()) {
        return { status: 422, ok: false, body: { errorCode: "invalid_request" } };
      }
      existing.generationCount = (existing.generationCount || 0) + 1;
      const now = new Date().toISOString();
      existing.versions = [...(existing.versions || []), { versionId: String(existing.generationCount), createdAtUtc: now, url: versionUrl(code, existing.reportNumber, String(existing.generationCount)) }];
      existing.etag = `"etag-${code}-${existing.reportNumber}-${Date.now()}"`;
      return { status: 200, ok: true, body: { reportNumber: existing.reportNumber, reportStatus: existing.reportStatus, generationCount: existing.generationCount, llmRunId: "fixture-run" } };
    }
    const reportNumber = nextReportNumber[code]++;
    const now = new Date().toISOString();
    const row = reportRow({
      code,
      reportNumber,
      weekStart: body.weekStart,
      reportStatus: "Draft",
      generationCount: 1,
      dataConfirmedAtUtc: now,
      dataConfirmedBy: "sm@example.com",
      generatedAtUtc: now,
      versions: [{ versionId: "1", createdAtUtc: now, url: versionUrl(code, reportNumber, "1") }],
    });
    project.reports = [row, ...(project.reports || [])];
    return { status: 200, ok: true, body: { reportNumber, reportStatus: "Draft", generationCount: 1, llmRunId: "fixture-run" } };
  }

  /** Fixture reproduction of PATCH /api/projects/{code}/reports/weekly/{reportNumber} (§4.6 transition matrix). */
  async function patchReportWeekly(code, reportNumber, body) {
    if (periodAware) calls.push({ call: "patchReportWeekly", code, reportNumber, action: body.action, etag: body.etag });
    if (body.action === "approve" || body.action === "publish" || body.action === "render_pdf") await delay();
    const row = findReport(code, reportNumber);
    if (!row) return { status: 404, ok: false, body: { errorCode: "report_not_found" } };
    if (String(body.etag) !== row.etag) return { status: 409, ok: false, body: { errorCode: "stale_etag", row } };
    if (body.action === "approve") {
      if (row.reportStatus !== "Draft") return { status: 422, ok: false, body: { errorCode: "invalid_transition" } };
      // Browser tests set window.__fixtureApproveFail to see an approval the server refuses.
      if (browser.__fixtureApproveFail) return { status: 422, ok: false, body: { errorCode: browser.__fixtureApproveFail, row } };
      row.reportStatus = "Approved";
      row.approvedAtUtc = new Date().toISOString();
      row.approvedBy = ME;
      // Browser tests set window.__fixturePdfFail to see the "PDF not created" state.
      const pdfFails = periodAware && typeof window !== "undefined" && window.__fixturePdfFail === true;
      row.pdfUrl = pdfFails ? "" : `https://negmarine.sharepoint.com/sites/ConstructionSupervision/WeeklyReports-${code}/PDF/${code}_Progress_Report_${row.weekStart}.pdf`;
      row.pdfStatus = pdfFails ? "failed" : "ready";
      row.etag = `"etag-${code}-${reportNumber}-${Date.now()}"`;
      return { status: 200, ok: true, body: { ...row, pdf: pdfFails ? { status: "failed" } : { status: "ready", url: row.pdfUrl } } };
    }
    if (body.action === "reopen") {
      if (row.reportStatus !== "Approved" || !String(body.reason || "").trim()) {
        return { status: 422, ok: false, body: { errorCode: "invalid_transition" } };
      }
      row.reportStatus = "Draft";
      row.pdfUrl = "";
      row.pdfStatus = "none";
      row.etag = `"etag-${code}-${reportNumber}-${Date.now()}"`;
      return { status: 200, ok: true, body: row };
    }
    if (body.action === "publish") {
      if (row.reportStatus !== "Approved") return { status: 422, ok: false, body: { errorCode: "invalid_transition" } };
      if (row.publishedUrl) return { status: 409, ok: false, body: { errorCode: "already_published", url: row.publishedUrl } };
      // Browser tests set window.__fixturePublishFail to see the no-overwrite refusals.
      if (browser.__fixturePublishFail === "already_published") {
        return { status: 409, ok: false, body: { errorCode: "already_published", url: "https://negmarine.sharepoint.com/sites/ss418/Weekly%20Reports/existing.pdf" } };
      }
      if (browser.__fixturePublishFail === "readback_mismatch") return { status: 500, ok: false, body: { errorCode: "readback_mismatch" } };
      row.reportStatus = "Issued";
      row.publishedNumber = (byCode[code].reports || []).reduce((m, r) => Math.max(m, Number(r.publishedNumber) || 0), 0) + 1;
      row.issuedAtUtc = new Date().toISOString();
      row.publishedUrl = `https://negmarine.sharepoint.com/sites/ConstructionSupervision/Weekly%20Reports/${code}/${code}-WR-${row.weekStart}.docx`;
      row.etag = `"etag-${code}-${reportNumber}-${Date.now()}"`;
      return { status: 200, ok: true, body: row };
    }
    if (body.action === "render_pdf") {
      if (row.reportStatus !== "Approved") return { status: 422, ok: false, body: { errorCode: "invalid_transition" } };
      if (browser.__fixturePdfFail === true) return { status: 502, ok: false, body: { errorCode: "pdf_conversion_failed", row } };
      row.pdfUrl = row.pdfUrl || `https://negmarine.sharepoint.com/sites/ConstructionSupervision/WeeklyReports-${code}/PDF/${code}_Progress_Report_${row.weekStart}.pdf`;
      row.pdfStatus = "ready";
      row.etag = `"etag-${code}-${reportNumber}-${Date.now()}"`;
      return { status: 200, ok: true, body: { ...row, pdf: { status: "ready", url: row.pdfUrl } } };
    }
    return { status: 422, ok: false, body: { errorCode: "invalid_transition" } };
  }

  const api = { fetchReports, postReportWeekly, fetchReportStatus, patchReportWeekly, waivePhoto, remindPhoto, calls };
  // Browser tests reach the store to prove what the server would refuse (a second generation).
  if (v2 && typeof window !== "undefined") window.__fixtureReportStore = api;
  return api;
}
